import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import { API } from "aws-amplify";

interface Props {
  profileId: string;
  drugAsin: string;
  onExpireSuccess: () => void;
}

interface State {
  isExpiring: boolean;
  expireResult: any | null;
  confirmed: boolean;
  error: string | null;
}

class ExpireOverridesSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isExpiring: false,
      expireResult: null,
      confirmed: false,
      error: null,
    };
  }

  private handleExpire = async () => {
    if (this.state.isExpiring) return;

    const { profileId, drugAsin, onExpireSuccess } = this.props;
    this.setState({ isExpiring: true, error: null, expireResult: null });

    try {
      const response = await API.post("ApaqPortalAPI", "/override/expire", {
        body: { profileId, drugAsin },
      });
      this.setState({ isExpiring: false, expireResult: response, confirmed: false });
      onExpireSuccess();
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to expire overrides";
      this.setState({ isExpiring: false, error: message, confirmed: false });
    }
  };

  render() {
    const { drugAsin } = this.props;
    const { isExpiring, expireResult, confirmed, error } = this.state;

    return (
      <Container header={
        <Header
          variant="h3"
          actions={
            <Button
              variant="primary"
              onClick={() => {
                if (confirmed) {
                  this.handleExpire();
                } else {
                  this.setState({ confirmed: true, expireResult: null });
                }
              }}
              loading={isExpiring}
            >
              {confirmed ? "Confirm Expire" : "Expire All Overrides"}
            </Button>
          }
        >
          Expire Overrides - {drugAsin}
        </Header>
      }>
        <SpaceBetween size="m">
          {!confirmed && !expireResult && (
            <Box color="text-body-secondary">
              This will expire all ACTIVE and CONSUMED overrides for the selected drug.
              ACTIVE overrides will have their earliest order date backdated 1 day.
              CONSUMED overrides will have their creation date backdated 13 months.
            </Box>
          )}

          {confirmed && !isExpiring && (
            <Flashbar items={[{
              type: "warning",
              content: "Are you sure? This action cannot be undone. Click 'Confirm Expire' to proceed.",
              dismissible: true,
              onDismiss: () => this.setState({ confirmed: false }),
            }]} />
          )}

          {error && (
            <Flashbar items={[{
              type: "error",
              content: error,
              dismissible: true,
              onDismiss: () => this.setState({ error: null }),
            }]} />
          )}

          {expireResult && (
            <div>
              <Flashbar items={[{
                type: "success",
                content: `Successfully expired ${expireResult.updatedRecords?.length || 0} override(s).`,
              }]} />
              <pre style={{
                backgroundColor: "#f5f5f5",
                border: "1px solid lightgray",
                borderRadius: "4px",
                padding: "10px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "normal",
                marginTop: "12px",
              }}>
                {JSON.stringify(expireResult, null, 2)}
              </pre>
            </div>
          )}
        </SpaceBetween>
      </Container>
    );
  }
}

export default ExpireOverridesSection;
